import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchCoachLogs, fetchCoachLog } from '../lib/api';
import { ChevronRight, ChevronDown, CheckCircle2, XCircle, MessageSquare, RotateCcw } from 'lucide-react';
import PageSkeleton from './nav/PageSkeleton';

/**
 * PaginaCoachHistory — storico + audit log delle conversazioni con QuantumCoach.
 *
 * 2 modalità:
 *   - lista (route /coach-history): tabella riga-per-riga delle sessioni
 *   - dettaglio (route /coach-history/:id): chat read-only + lista operazioni
 *
 * I dati arrivano dalla tabella `coach_log` (migration 007). Best-effort:
 * se la tabella non esiste, mostra empty state con istruzioni.
 */
export default function PaginaCoachHistory() {
  const { id } = useParams();
  return id ? <Detail id={id} /> : <List />;
}

const MOMENTO_LABEL = {
  setup: 'Setup',
  mese: 'Mese',
  settimana: 'Settimana',
  quick: 'Quick',
};

function List() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCoachLogs()
      .then((r) => setItems(r.items || []))
      .catch((e) => setError(e?.response?.data?.detail || 'Errore caricamento storico'));
  }, []);

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="coach-history-list">
      <header className="mb-8">
        <h1 className="font-display text-2xl md:text-3xl tracking-tight">Storico Coach</h1>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute mt-1">
          conversazioni · operazioni applicate
        </p>
      </header>

      {error && (
        <div className="border border-risk/20 bg-risk/10 text-risk p-4 text-sm mb-6">
          {error}
        </div>
      )}

      {items === null && !error && (
        <PageSkeleton variant="list" inline sections={2} rowsPerSection={6} testid="coach-history-skeleton" />
      )}

      {items !== null && items.length === 0 && (
        <div className="border border-linesoft bg-canvas p-10 text-center">
          <MessageSquare size={32} strokeWidth={1.5} className="mx-auto text-mute mb-3" />
          <h2 className="font-display text-lg mb-2">Ancora nessuna conversazione salvata</h2>
          <p className="text-sm text-soft max-w-md mx-auto">
            Lo storico viene popolato automaticamente quando confermi o annulli
            un suggerimento del coach in Setup, Mese o Settimana.
          </p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="border border-linesoft">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-canvas">
                <th className="text-left py-2 px-3 font-mono text-xs uppercase tracking-[0.15em] text-soft">Quando</th>
                <th className="text-left py-2 px-3 font-mono text-xs uppercase tracking-[0.15em] text-soft">Momento</th>
                <th className="text-left py-2 px-3 font-mono text-xs uppercase tracking-[0.15em] text-soft">Esito</th>
                <th className="text-right py-2 px-3 font-mono text-xs uppercase tracking-[0.15em] text-soft">Operazioni</th>
                <th className="py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-linesoft hover:bg-canvas"
                  data-testid="coach-log-row"
                >
                  <td className="py-3 px-3 font-mono text-xs text-soft">{formatDate(row.created_at)}</td>
                  <td className="py-3 px-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft border border-line px-2 py-0.5">
                      {MOMENTO_LABEL[row.momento] || row.momento}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    {row.esito === 'applicato' ? (
                      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ok">
                        <CheckCircle2 size={12} strokeWidth={1.8} /> Applicato
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-soft">
                        <XCircle size={12} strokeWidth={1.8} /> Annullato
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-right font-mono tabular-nums text-soft">
                    {row.ops_count}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <Link
                      to={`/coach-history/${row.id}`}
                      className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink hover:text-accent inline-flex items-center gap-1"
                      data-testid="coach-log-open"
                    >
                      Apri <ChevronRight size={12} strokeWidth={1.8} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Detail({ id }) {
  const [row, setRow] = useState(null);
  const [error, setError] = useState(null);
  const [showRawOps, setShowRawOps] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCoachLog(id)
      .then(setRow)
      .catch((e) => setError(e?.response?.data?.detail || 'Sessione non trovata'));
  }, [id]);

  const handleResume = () => {
    if (!row) return;
    // Salva il payload nello sessionStorage; la chat lo legge al mount.
    try {
      sessionStorage.setItem(
        `qp.coach.resume.${row.momento}`,
        JSON.stringify({
          id: row.id,
          messaggi: row.messaggi || [],
          loggedAt: row.created_at,
        }),
      );
    } catch { /* quota piena, ignora */ }
    const route = row.momento === 'setup' ? '/setup'
      : row.momento === 'mese' ? '/mese'
      : row.momento === 'settimana' ? '/settimana'
      : '/';
    navigate(route);
  };

  if (error) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-10">
        <Link to="/coach-history" className="font-mono text-[11px] uppercase tracking-[0.2em] text-soft hover:text-ink">← Storico</Link>
        <div className="border border-risk/20 bg-risk/10 text-risk p-4 text-sm mt-4">{error}</div>
      </div>
    );
  }
  if (!row) {
    return <PageSkeleton variant="list" sections={2} rowsPerSection={5} testid="coach-history-detail-skeleton" />;
  }

  const messaggi = row.messaggi || [];
  const operazioni = row.operazioni || [];

  return (
    <div className="max-w-[900px] mx-auto px-6 py-10" data-testid="coach-history-detail">
      <Link to="/coach-history" className="font-mono text-[11px] uppercase tracking-[0.2em] text-soft hover:text-ink">← Storico</Link>

      <header className="mt-4 mb-8 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl tracking-tight">
            QuantumCoach · {MOMENTO_LABEL[row.momento] || row.momento}
          </h1>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute mt-1">
            {formatDate(row.created_at)}
          </p>
        </div>
        <span
          className={
            'inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[11px] uppercase tracking-[0.18em] border ' +
            (row.esito === 'applicato'
              ? 'border-ok/20 bg-ok/10 text-ok'
              : 'border-line bg-canvas text-soft')
          }
          data-testid="coach-log-esito"
        >
          {row.esito === 'applicato' ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
          {row.esito === 'applicato' ? 'Operazioni applicate' : 'Annullato'}
        </span>
      </header>

      {/* Riprendi: pre-carica i messaggi nella chat corrispondente */}
      {row.momento !== 'quick' && (row.messaggi || []).length > 0 && (
        <div className="mb-6 -mt-4">
          <button
            type="button"
            onClick={handleResume}
            className="inline-flex items-center gap-2 px-4 py-2 border border-line hover:border-ink hover:bg-linesoft font-mono text-xs uppercase tracking-[0.18em] text-soft hover:text-ink transition-colors"
            data-testid="coach-log-resume"
            title={`Apri /${row.momento} con questa conversazione pre-caricata come contesto`}
          >
            <RotateCcw size={14} strokeWidth={1.5} />
            Riprendi questa conversazione
          </button>
        </div>
      )}

      {/* Chat read-only */}
      <section className="mb-10">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute mb-3">Conversazione</h2>
        {messaggi.length === 0 ? (
          <p className="text-sm text-soft">Nessun messaggio salvato.</p>
        ) : (
          <div className="space-y-4">
            {messaggi.map((m, i) => (
              <div key={`msg-${i}-${m.role}-${(m.content || '').slice(0, 16)}`} className={m.role === 'user' ? 'flex justify-end' : 'flex'}>
                <div
                  className={
                    m.role === 'user'
                      ? 'max-w-[80%] bg-ink text-canvas px-4 py-2 font-body text-sm leading-relaxed'
                      : 'flex-1 font-body text-[15px] leading-relaxed whitespace-pre-wrap text-ink'
                  }
                >
                  {m.role !== 'user' && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mb-1">QuantumCoach</div>
                  )}
                  {(m.content || '').replace(/\\n/g, '\n')}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Audit operazioni */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute">
            Operazioni {row.esito === 'applicato' ? 'salvate' : 'proposte (non applicate)'} · {operazioni.length}
          </h2>
          {operazioni.length > 0 && (
            <button
              type="button"
              onClick={() => setShowRawOps((v) => !v)}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft hover:text-ink inline-flex items-center gap-1"
              data-testid="coach-log-toggle-raw"
            >
              {showRawOps ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {showRawOps ? 'JSON' : 'JSON'}
            </button>
          )}
        </div>

        {operazioni.length === 0 ? (
          <p className="text-sm text-soft">Nessuna operazione registrata.</p>
        ) : showRawOps ? (
          <pre className="border border-line bg-canvas p-3 text-xs overflow-auto font-mono max-h-[400px]">
            {JSON.stringify(operazioni, null, 2)}
          </pre>
        ) : (
          <ul className="space-y-2">
            {operazioni.map((op, i) => (
              <li key={op.id || `${op.entita}-${op.azione}-${i}`} className="border border-linesoft p-3 font-mono text-xs leading-relaxed" data-testid="coach-log-op">
                <span className="inline-block bg-ink text-canvas px-1.5 py-0.5 mr-2 uppercase tracking-wider text-[10px]">
                  {op.azione || 'op'}
                </span>
                <span className="text-soft">{op.entita}</span>
                {op.id && <span className="text-mute"> · {op.id.slice(0, 8)}…</span>}
                {op.campi && <OpFieldsPreview campi={op.campi} />}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Sotto-componente che memoizza l'estrazione di max 6 campi pubblici da `op.campi`. */
function OpFieldsPreview({ campi }) {
  const entries = useMemo(
    () => Object.entries(campi).filter(([k]) => !k.startsWith('_')).slice(0, 6),
    [campi],
  );
  return (
    <div className="mt-1 ml-2 text-soft">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <span className="text-mute">{k}:</span>
          <span className="text-ink truncate">{typeof v === 'string' ? v : JSON.stringify(v)}</span>
        </div>
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' });
    const time = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
}
